// Shared display helpers for the payments surfaces (member + club admin).
// The API serialises enums as NUMBERS (no JsonStringEnumConverter), so the
// maps below mirror SLL.Api/Models/Entities/PaymentCharge.cs and
// ClubPaymentAccount.cs exactly. Amounts are minor units (AmountMinor).

// PaymentChargeStatus (SLL.Api PaymentCharge.cs)
export const ChargeStatus = {
  Pending: 0,
  Processing: 1,
  Succeeded: 2,
  Retrying: 3,
  Failed: 4,
  Refunded: 5,
  PartiallyRefunded: 6,
  SettledOffline: 7,
  Waived: 8,
  Disputed: 9,
  // LEGACY (24 Sep 2026): once meant "nothing charged, the payer is under 18,
  // the club arranges it". Under-18s now pay like anyone else and nothing sets
  // it any more; kept so a stored row still reads sensibly for the club.
  ClubToArrange: 10,
};

// A PaymentCharge row is minted the moment a payment INTENT is created, so
// merely OPENING a pay form leaves a Pending row behind, and abandoning it
// leaves a Processing one forever. Those are not money and not actionable, so
// charge LISTS show only charges that RESOLVED: took money, or failed trying.
// The Upcoming tab is the deliberate exception — it exists to show charges that
// are created-but-not-yet-run.
export const RESOLVED_CHARGE_STATUSES = [
  ChargeStatus.Succeeded,
  ChargeStatus.Retrying,
  ChargeStatus.Failed,
  ChargeStatus.Refunded,
  ChargeStatus.PartiallyRefunded,
  ChargeStatus.SettledOffline,
  ChargeStatus.Waived,
  ChargeStatus.Disputed,
  ChargeStatus.ClubToArrange,
];
export const isResolvedCharge = (charge) =>
    RESOLVED_CHARGE_STATUSES.includes(charge?.status);

// ClubPaymentAccountStatus (SLL.Api ClubPaymentAccount.cs)
export const AccountStatus = {
  Created: 0,
  OnboardingStarted: 1,
  DetailsSubmitted: 2,
  ChargesEnabled: 3,
  Restricted: 4,
  Disconnected: 5,
};

// PaymentChargeResolution (SLL.Api PaymentRequests.cs)
export const ChargeResolution = {
  SettleOffline: 0,
  Waive: 1,
};

// Status chip vocabulary (mockup canon: one meaning per colour, app-wide —
// Succeeded ok · Retrying/Disputed warn · Failed brand · the rest neutral).
const chargeStatusMeta = {
  [ChargeStatus.Pending]: {label: 'Pending', chipClass: '', icon: null},
  [ChargeStatus.Processing]: {label: 'Processing', chipClass: '', icon: 'mdi-progress-clock'},
  [ChargeStatus.Succeeded]: {label: 'Succeeded', chipClass: 'tl-chip--ok', icon: 'mdi-check'},
  [ChargeStatus.Retrying]: {label: 'Retrying', chipClass: 'tl-chip--warn', icon: 'mdi-clock-outline'},
  [ChargeStatus.Failed]: {label: 'Failed', chipClass: 'tl-chip--brand', icon: 'mdi-alert-circle-outline'},
  [ChargeStatus.Refunded]: {label: 'Refunded', chipClass: '', icon: 'mdi-undo'},
  [ChargeStatus.PartiallyRefunded]: {label: 'Partially refunded', chipClass: '', icon: 'mdi-undo'},
  [ChargeStatus.SettledOffline]: {label: 'Settled offline', chipClass: '', icon: null},
  [ChargeStatus.Waived]: {label: 'Waived', chipClass: '', icon: null},
  [ChargeStatus.Disputed]: {label: 'Disputed', chipClass: 'tl-chip--warn', icon: 'mdi-gavel'},
  [ChargeStatus.ClubToArrange]: {label: 'Club to arrange', chipClass: 'tl-chip--warn', icon: 'mdi-account-clock-outline'},
};

const accountStatusMeta = {
  [AccountStatus.Created]: {label: 'Setting up', chipClass: 'tl-chip--warn', icon: 'mdi-clock-outline'},
  [AccountStatus.OnboardingStarted]: {label: 'Setting up', chipClass: 'tl-chip--warn', icon: 'mdi-clock-outline'},
  [AccountStatus.DetailsSubmitted]: {label: 'Stripe is verifying', chipClass: 'tl-chip--warn', icon: 'mdi-clock-outline'},
  [AccountStatus.ChargesEnabled]: {label: 'Charges enabled', chipClass: 'tl-chip--ok', icon: 'mdi-check'},
  [AccountStatus.Restricted]: {label: 'Action needed', chipClass: 'tl-chip--brand', icon: 'mdi-alert-circle-outline'},
  [AccountStatus.Disconnected]: {label: 'Disconnected', chipClass: 'tl-chip--brand', icon: 'mdi-link-off'},
};

// ── Admin money-action copy (ADMIN_UX_AUDIT A6 / B2 / N5) — pure, unit tested ──

const formatMoney = (amountMinor, currency) => {
  const code = (currency || 'gbp').toUpperCase();
  try {
    return new Intl.NumberFormat('en-GB', {style: 'currency', currency: code}).format((amountMinor ?? 0) / 100);
  } catch {
    return `${((amountMinor ?? 0) / 100).toFixed(2)} ${code}`;
  }
};

// EntryFeeService.SourceType* — the line-item source types of entry fees.
export const ENTRY_FEE_SOURCE_TYPES = ['LadderEntry', 'LeagueCycleEntry', 'TournamentEntry', 'AmericanoCycleEntry'];

/**
 * A6: refunding the WHOLE of what remains on a charge that carries an entry fee
 * puts that fee back to Due (StripeWebhookService.HandleChargeRefunded) — the
 * refund dialog must say so. Partial refunds leave the fee Paid.
 */
export const refundMakesFeeDueAgain = (lineItems, refundMinor, remainingMinor) =>
    (lineItems ?? []).some((l) => ENTRY_FEE_SOURCE_TYPES.includes(l?.sourceType))
    && refundMinor > 0
    && refundMinor >= remainingMinor;

/** B2: the Retry now confirmation — amount and whose card. */
export const retryConfirmText = (charge) =>
    `Try the ${formatMoney(charge?.amountMinor, charge?.currency)} charge for ${charge?.userName ?? 'this member'} again now? It uses the card they have saved, if any.`;

/**
 * B2: the toast after Retry now, from the charge the API returns.
 * → {message, color?} for setAlertMessage (color 'error' when the card wasn't charged).
 */
export const retryOutcomeAlert = (charge, result) => {
  const who = charge?.userName ?? 'the member';
  const amount = formatMoney(charge?.amountMinor, charge?.currency);
  if (result?.status === ChargeStatus.Succeeded) return {message: `Charged ${amount} to ${who}'s card`};
  if (result?.status === ChargeStatus.Processing || result?.status === ChargeStatus.Pending) {
    return {message: `Charging ${amount} to ${who}'s card. It's processing`};
  }
  return {
    message: `${who}'s card was not charged${result?.failureMessage ? `: ${result.failureMessage}` : ''}`,
    color: 'error',
  };
};

/** B2: the toast after a refund. */
export const refundedAlertText = (result, charge) => {
  const who = charge?.userName ?? 'the member';
  return result?.amountMinor != null
      ? `Refunded ${formatMoney(result.amountMinor, result.currency ?? charge?.currency)} to ${who}`
      : `Refund issued to ${who}`;
};

/** N5: Retrying already blocks paid sign-ups (ChargePipeline.IsBlocked counts Retrying + Failed). */
/**
 * For club admins: a charge the club is still owed and can act on (pay link,
 * settle offline, waive). Failed and retrying charges, plus any stored legacy
 * Club to arrange row. Only the first two can be retried on a card.
 */
export const isOwedToTheClub = (charge) =>
    charge?.status === ChargeStatus.Failed
    || charge?.status === ChargeStatus.Retrying
    || charge?.status === ChargeStatus.ClubToArrange;

export const blocksPaidEvents = (charge) =>
    charge?.status === ChargeStatus.Failed || charge?.status === ChargeStatus.Retrying;

/**
 * B2: a meet billing member whose sessions are ALL bookings on dates not yet
 * finalised owes nothing yet ("Upcoming — not yet due").
 */
export const isUpcomingOnlyMember = (m) =>
    (m?.sessionCount ?? 0) > 0 && (m?.projectedSessionCount ?? 0) >= (m?.sessionCount ?? 0);

// The club's commission as the server labels it (C5-9): "2%", or "2% + VAT"
// once VAT is charged (Payments plan v3). With no label from the server, show
// none rather than a guessed rate.
export const commissionRateLabel = (label) =>
    label ? `${label} per charge` : 'Shown once set up';

export function usePaymentsDisplay() {
  /** "gbp" + 650 → "£6.50" (minor units → localised currency string). */
  const money = (amountMinor, currency) => {
    const code = (currency || 'gbp').toUpperCase();
    try {
      return new Intl.NumberFormat('en-GB', {style: 'currency', currency: code})
          .format((amountMinor ?? 0) / 100);
    } catch {
      return `${((amountMinor ?? 0) / 100).toFixed(2)} ${code}`;
    }
  };

  const chargeStatus = (status) =>
      chargeStatusMeta[status] ?? {label: `Status ${status}`, chipClass: '', icon: null};

  const accountStatus = (status) =>
      accountStatusMeta[status] ?? {label: `Status ${status}`, chipClass: '', icon: null};

  /** Failed-final or still on the retry ladder — the states that need recovery. */
  const isOutstanding = (charge) =>
      charge.status === ChargeStatus.Failed || charge.status === ChargeStatus.Retrying;

  /** Refundable = money was taken and not fully returned; blocked while disputed. */
  const isRefundable = (charge) =>
      (charge.status === ChargeStatus.Succeeded || charge.status === ChargeStatus.PartiallyRefunded)
      && charge.refundedMinor < charge.amountMinor;

  /**
   * The API's handled errors are {global:[...], field:[...]} — but a raw
   * Response (non-JSON body) can also be thrown. Normalise so
   * <crud-error-display> always has something to render.
   */
  const normalizeErrors = (e, fallback = 'Something went wrong. Please try again') => {
    if (e && typeof e === 'object' && !((typeof Response !== 'undefined') && e instanceof Response)
        && Object.keys(e).length > 0) {
      return e.global || Object.keys(e).some((k) => Array.isArray(e[k])) ? e : {global: [fallback]};
    }
    return {global: [fallback]};
  };

  const cardLabel = (method) => {
    const brand = method?.brand
        ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1)
        : 'Card';
    return `${brand} ···· ${method?.last4 ?? '????'}`;
  };

  const cardExpiry = (method) =>
      `${String(method?.expMonth ?? 0).padStart(2, '0')}/${String((method?.expYear ?? 0) % 100).padStart(2, '0')}`;

  const isCardExpired = (method) => {
    if (!method?.expYear) return false;
    const now = new Date();
    return method.expYear < now.getFullYear()
        || (method.expYear === now.getFullYear() && method.expMonth < now.getMonth() + 1);
  };

  return {
    ChargeStatus,
    AccountStatus,
    ChargeResolution,
    RESOLVED_CHARGE_STATUSES,
    isResolvedCharge,
    money,
    chargeStatus,
    accountStatus,
    isOutstanding,
    isRefundable,
    normalizeErrors,
    cardLabel,
    cardExpiry,
    isCardExpired,
  };
}
